// components/ui/Select.jsx - Shadcn style Select
import React, { useState } from "react";
import { ChevronDown, Check, Search, X } from "lucide-react";
import { cn } from "../../utils/cn";

import Input from "./Input";

const Select = React.forwardRef(({
    className,
    options = [],
    value,
    defaultValue,
    placeholder = "Select an option",
    multiple = false,
    disabled = false,
    required = false,
    label,
    description,
    error,
    searchable = false,
    clearable = false,
    loading = false,
    id,
    name,
    onChange,
    onOpenChange,
    ...props
}, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Generate unique ID if not provided
    const selectId = id || `select-${Math.random()?.toString(36)?.substr(2, 9)}`;

    // Filter options based on search
    const filteredOptions = searchable && searchTerm
        ? options?.filter(option =>
            option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
            (option?.value && option?.value?.toString()?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
        )
        : options;

    // Get selected option(s) for display
    const getSelectedDisplay = () => {
        if (!value) return placeholder;

        if (multiple) {
            const selectedOptions = options?.filter(opt => value?.includes(opt?.value));
            if (selectedOptions?.length === 0) return placeholder;
            if (selectedOptions?.length === 1) return selectedOptions?.[0]?.label;
            return `${selectedOptions?.length} items selected`;
        }

        const selectedOption = options?.find(opt => opt?.value === value);
        return selectedOption ? selectedOption?.label : placeholder;
    };

    const handleToggle = () => {
        if (!disabled) {
            const newIsOpen = !isOpen;
            setIsOpen(newIsOpen);
            onOpenChange?.(newIsOpen);
            if (!newIsOpen) {
                setSearchTerm("");
            }
        }
    };

    const handleOptionSelect = (option) => {
        if (multiple) {
            const newValue = value || [];
            const updatedValue = newValue?.includes(option?.value)
                ? newValue?.filter(v => v !== option?.value)
                : [...newValue, option?.value];
            onChange?.(updatedValue);
        } else {
            onChange?.(option?.value);
            setIsOpen(false);
            onOpenChange?.(false);
        }
    };

    const handleClear = (e) => {
        e?.stopPropagation();
        onChange?.(multiple ? [] : '');
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e?.target?.value);
    };

    const isSelected = (optionValue) => {
        if (multiple) {
            return value?.includes(optionValue) || false;
        }
        return value === optionValue;
    };

    const hasValue = multiple ? value?.length > 0 : value !== undefined && value !== '';

    return (
        <div className={cn("relative w-full", className)} ref={ref}>
            {/* Label */}
            {label && (
                <label
                    htmlFor={selectId}
                    className={cn(
                        "block text-sm font-medium mb-1.5",
                        error ? "text-red-400" : "text-foreground",
                        disabled && "opacity-50 cursor-not-allowed"
                    )}
                >
                    {label}
                    {required && <span className="text-red-400 mr-1">*</span>}
                </label>
            )}

            {/* Select Trigger */}
            <button
                id={selectId}
                type="button"
                onClick={handleToggle}
                disabled={disabled}
                className={cn(
                    "flex h-10 w-full items-center justify-between rounded-md border bg-black px-3 py-2 text-sm text-white",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-black",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    "transition-all duration-200",
                    error
                        ? "border-red-500/50 focus-visible:ring-red-500" :"border-white/20 hover:border-gold/40",
                    !value && "text-white/40"
                )}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                {...props}
            >
                <span className="truncate">{getSelectedDisplay()}</span>
                <div className="flex items-center gap-1">
                    {clearable && value && (
                        <X
                            className="h-4 w-4 text-gold hover:text-gold-light transition-colors"
                            onClick={(e) => {
                                e?.stopPropagation();
                                handleClear();
                            }}
                        />
                    )}
                    <ChevronDown
                        className={cn(
                            "h-4 w-4 text-gold transition-transform duration-200",
                            isOpen && "transform rotate-180"
                        )}
                    />
                </div>
            </button>

            {/* Hidden native select for form submission */}
            <select
                name={name}
                value={value || ''}
                onChange={() => { }} // Controlled by our custom logic
                className="sr-only"
                tabIndex={-1}
                multiple={multiple}
                required={required}
            >
                <option value="">Select...</option>
                {options?.map(option => (
                    <option key={option?.value} value={option?.value}>
                        {option?.label}
                    </option>
                ))}
            </select>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-black border-2 border-gold/30 rounded-md shadow-2xl max-h-60 overflow-hidden">
                    {/* Search Input */}
                    {searchable && (
                        <div className="p-2 border-b border-gold/20">
                            <div className="relative">
                                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gold" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e?.target?.value)}
                                    placeholder="Search..."
                                    className="w-full h-8 pr-9 pl-3 bg-white/5 border border-white/20 rounded text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                                    onClick={(e) => e?.stopPropagation()}
                                />
                            </div>
                        </div>
                    )}

                    {/* Options List */}
                    <div className="overflow-y-auto max-h-48 p-1">
                        {loading ? (
                            <div className="py-6 text-center text-sm text-white/60">Loading...</div>
                        ) : filteredOptions?.length === 0 ? (
                            <div className="py-6 text-center text-sm text-white/60">No options found</div>
                        ) : (
                            filteredOptions?.map((option) => {
                                const isSelected = multiple
                                    ? value?.includes(option?.value)
                                    : value === option?.value;

                                return (
                                    <button
                                        key={option?.value}
                                        type="button"
                                        onClick={() => handleOptionSelect(option)}
                                        className={cn(
                                            "w-full flex items-center justify-between px-3 py-2 text-sm rounded transition-colors",
                                            isSelected
                                                ? "bg-gold/20 text-gold font-medium" :"text-white hover:bg-gold/10 hover:text-gold"
                                        )}
                                    >
                                        <span className="truncate">{option?.label}</span>
                                        {isSelected && <Check className="h-4 w-4 text-gold flex-shrink-0" />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                    <X className="h-3 w-3" />
                    {error}
                </p>
            )}

            {/* Description */}
            {description && !error && (
                <p className="mt-1.5 text-xs text-white/60">{description}</p>
            )}
        </div>
    );
});

Select.displayName = "Select";

export default Select;